import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarBackgroundClass, getAvatarBackgroundStyle } from "@/lib/avatarBackground";
import { cn } from "@/lib/utils";

type ProfileAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

const SIZE_CLASSES: Record<ProfileAvatarSize, string> = {
  xs: "h-8 w-8 text-xs",
  sm: "h-10 w-10 text-sm",
  md: "h-12 w-12 text-sm",
  lg: "h-16 w-16 text-lg",
  xl: "h-24 w-24 text-2xl",
};

interface ProfileAvatarProps {
  /** Used to pick the deterministic background colour. */
  userId?: string | null;
  avatarUrl?: string | null;
  name?: string | null;
  background?: string | null;
  size?: ProfileAvatarSize;
  className?: string;
}

const initialsOf = (name?: string | null) => {
  if (!name) return "U";
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

/**
 * The single avatar used everywhere: same framing (full head, never squashed),
 * same deterministic background, same initials fallback.
 */
export const ProfileAvatar = ({
  userId,
  avatarUrl,
  name,
  background,
  size = "md",
  className,
}: ProfileAvatarProps) => (
  <Avatar
    style={getAvatarBackgroundStyle(background)}
    className={cn(
      SIZE_CLASSES[size],
      "relative shrink-0 overflow-hidden border border-border",
      getAvatarBackgroundClass(background),
      className,
    )}
  >
    {avatarUrl ? (
      <AvatarImage
        src={avatarUrl}
        alt={name ? `${name} avatar` : "Profile avatar"}
        /* The artwork is a tall full-body render; this frames crown-to-shoulders. */
        className="absolute left-[13%] top-[-8%] aspect-auto h-auto w-[74%] max-w-none object-contain"
      />
    ) : null}

    <AvatarFallback className="bg-transparent font-semibold text-foreground">
      {initialsOf(name)}
    </AvatarFallback>
  </Avatar>
);

export default ProfileAvatar;
